'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetWinnerRow, useGetWinner } from '@morfi/backend-sdk-v2/client';
import { APP_HEADER_STICKY_TOP_SIDEBAR_CLASS } from '@/components/layout/app-header-sticky-top';
import { MarketIdentitySection } from '@/components/market/identity/market-identity-section';
import { DirectTokenPurchaseAction } from '@/components/onramp/direct-purchase/direct-token-purchase-action';
import { DemoNotFoundCard } from '@/components/shared/demo-not-found-card';
import type { TradeFormProps } from '@/components/trade/trade-form';
import { AmmTradeForm } from '@/components/trade/trade-form';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Panel } from '@/components/ui/panel';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { DETAIL_SIDEBAR_W_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useDemoMode } from '@/providers/demo-mode-provider';
import { TradeExecutorProvider } from '@/providers/trade-executor-provider';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
import { winnerTradeDataFromRow } from '@/queries/winner-trade-data';
import { SimilarWinnersSection } from './similar-winners';
import { WinnerAboutCard } from './winner-about-card';
import { WinnerInfoModal } from './winner-info-modal';
import { WinnerMobileTrade } from './winner-mobile-trade';
import { WinnerOutcomeChart } from './winner-outcome-chart';
import { WinnerPositionPanel } from './winner-position-panel';
import { WinnerTabs } from './winner-tabs';
import { WinnerTokenActions } from './winner-token-actions';
import { WinnerTokenHeader } from './winner-token-header';
import { WinnerTokenStats, type WinnerTokenStatsSnapshot } from './winner-token-stats';
type WinnerTradeReserveProps = Pick<TradeFormProps, 'tradeData' | 'marketDataStale' | 'marketDataPending' | 'marketDataRefreshing' | 'onRetryMarketData'>;
interface WinnerLeftColumnProps {
  winner: GetWinnerRow;
  canonicalPath: string;
  poolSnapshot: OnchainTradeData;
}
function WinnerLeftColumn(t0) {
  const $ = _c(39);
  const {
    winner,
    canonicalPath,
    poolSnapshot
  } = t0;
  const marketPubkey = winner.parentMarket.pubkey;
  let t1;
  if ($[0] !== canonicalPath || $[1] !== winner.change24hBps || $[2] !== winner.effectiveMarketCapD6 || $[3] !== winner.graduatedAt || $[4] !== winner.imageNsfw || $[5] !== winner.imageUrl || $[6] !== winner.label || $[7] !== winner.mint || $[8] !== winner.parentMarket.marketType || $[9] !== winner.parentMarket.title || $[10] !== winner.price || $[11] !== winner.ticker) {
    t1 = <div className="order-1 lg:order-none"><WinnerTokenHeader mint={winner.mint} name={winner.label} marketTitle={winner.parentMarket.title} ticker={winner.ticker} imageUrl={winner.imageUrl} imageNsfw={winner.imageNsfw} marketType={winner.parentMarket.marketType} graduatedAtIso={winner.graduatedAt} marketCap={winner.effectiveMarketCapD6} price={winner.price} change24hBps={winner.change24hBps} canonicalPath={canonicalPath} /></div>;
    $[0] = canonicalPath;
    $[1] = winner.change24hBps;
    $[2] = winner.effectiveMarketCapD6;
    $[3] = winner.graduatedAt;
    $[4] = winner.imageNsfw;
    $[5] = winner.imageUrl;
    $[6] = winner.label;
    $[7] = winner.mint;
    $[8] = winner.parentMarket.marketType;
    $[9] = winner.parentMarket.title;
    $[10] = winner.price;
    $[11] = winner.ticker;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== marketPubkey || $[14] !== winner.effectiveMarketCapD6 || $[15] !== winner.graduatedAt || $[16] !== winner.indexedOutcomeMintSupplyD6 || $[17] !== winner.label || $[18] !== winner.mint || $[19] !== winner.outcomeIndex || $[20] !== winner.parentMarket.tradingStartedAt || $[21] !== winner.price) {
    t2 = <Panel density="compact" inheritBackground={true} surface="none" className="order-2 min-w-0 gap-4 py-0 lg:order-none"><div className="relative w-full"><WinnerOutcomeChart marketPubkey={marketPubkey} outcomeMint={winner.mint} originalOutcomeIndex={winner.outcomeIndex} tradingStartedAt={winner.parentMarket.tradingStartedAt} tradesFrom={winner.graduatedAt} label={winner.label} priceD9={winner.price} effectiveMarketCapD6={winner.effectiveMarketCapD6} indexedOutcomeMintSupplyD6={winner.indexedOutcomeMintSupplyD6} /></div></Panel>;
    $[13] = marketPubkey;
    $[14] = winner.effectiveMarketCapD6;
    $[15] = winner.graduatedAt;
    $[16] = winner.indexedOutcomeMintSupplyD6;
    $[17] = winner.label;
    $[18] = winner.mint;
    $[19] = winner.outcomeIndex;
    $[20] = winner.parentMarket.tradingStartedAt;
    $[21] = winner.price;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== winner.parentMarket) {
    t3 = <MarketIdentitySection identity={winner.parentMarket} />;
    $[23] = winner.parentMarket;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  let t4;
  if ($[25] !== marketPubkey || $[26] !== poolSnapshot) {
    t4 = <WinnerTabs marketPubkey={marketPubkey} poolSnapshot={poolSnapshot} />;
    $[25] = marketPubkey;
    $[26] = poolSnapshot;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== t3 || $[29] !== t4) {
    t5 = <div className="order-7 space-y-4 lg:order-none">{t3}{t4}</div>;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== marketPubkey) {
    t6 = <div className="order-8 lg:order-none"><SimilarWinnersSection marketPubkey={marketPubkey} /></div>;
    $[31] = marketPubkey;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div aria-hidden={true} className="order-9 h-20 lg:hidden" />;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t1 || $[35] !== t2 || $[36] !== t5 || $[37] !== t6) {
    t8 = <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-4">{t1}{t2}{t5}{t6}{t7}</div>;
    $[34] = t1;
    $[35] = t2;
    $[36] = t5;
    $[37] = t6;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
}
interface WinnerRightSidebarProps {
  winner: GetWinnerRow;
  canonicalPath: string;
  statsSnapshot: WinnerTokenStatsSnapshot;
  tradeFormOutcomes: TradeFormProps['outcomes'];
  tradeReserveProps: WinnerTradeReserveProps;
  tradeData: OnchainTradeData;
  mountTradeForm: boolean;
}
function WinnerRightSidebar(t0) {
  const $ = _c(33);
  const {
    winner,
    canonicalPath,
    statsSnapshot,
    tradeFormOutcomes,
    tradeReserveProps,
    tradeData,
    mountTradeForm
  } = t0;
  const marketPubkey = winner.parentMarket.pubkey;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(APP_HEADER_STICKY_TOP_SIDEBAR_CLASS, DETAIL_SIDEBAR_W_CLASS, "contents lg:sticky lg:flex lg:min-h-fit lg:shrink-0 lg:flex-col lg:gap-3 lg:self-start");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== marketPubkey || $[2] !== mountTradeForm || $[3] !== tradeFormOutcomes || $[4] !== tradeReserveProps || $[5] !== winner.outcomeIndex || $[6] !== winner.parentMarket.title) {
    t2 = mountTradeForm ? <div className="hidden lg:contents"><DirectTokenPurchaseAction marketAddress={marketPubkey} outcomeIndex={winner.outcomeIndex} origin="winner" /><AmmTradeForm marketAddress={marketPubkey} outcomes={tradeFormOutcomes} selectedOutcomeIndex={winner.outcomeIndex} marketStatus="graduated" allowGraduatedAmm={true} lockOutcome={true} hasHoldings={false} marketTitle={winner.parentMarket.title} {...tradeReserveProps} /></div> : null;
    $[1] = marketPubkey;
    $[2] = mountTradeForm;
    $[3] = tradeFormOutcomes;
    $[4] = tradeReserveProps;
    $[5] = winner.outcomeIndex;
    $[6] = winner.parentMarket.title;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== tradeData || $[9] !== winner.effectiveMarketCapD6 || $[10] !== winner.indexedOutcomeMintSupplyD6 || $[11] !== winner.mint || $[12] !== winner.outcomeIndex) {
    t3 = <WinnerPositionPanel winnerMint={winner.mint} outcomeIndex={winner.outcomeIndex} effectiveMarketCapD6={winner.effectiveMarketCapD6} indexedOutcomeMintSupplyD6={winner.indexedOutcomeMintSupplyD6} tradeData={tradeData} className="order-3 max-w-none lg:order-none" />;
    $[8] = tradeData;
    $[9] = winner.effectiveMarketCapD6;
    $[10] = winner.indexedOutcomeMintSupplyD6;
    $[11] = winner.mint;
    $[12] = winner.outcomeIndex;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== statsSnapshot) {
    t4 = <WinnerTokenStats snapshot={statsSnapshot} className="order-5 lg:order-none" />;
    $[14] = statsSnapshot;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== canonicalPath || $[17] !== marketPubkey || $[18] !== winner.label || $[19] !== winner.mint || $[20] !== winner.price) {
    t5 = <WinnerTokenActions marketPubkey={marketPubkey} mint={winner.mint} tokenName={winner.label} priceD9={winner.price} canonicalPath={canonicalPath} className="order-4 lg:order-none" />;
    $[16] = canonicalPath;
    $[17] = marketPubkey;
    $[18] = winner.label;
    $[19] = winner.mint;
    $[20] = winner.price;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== marketPubkey || $[23] !== winner.label || $[24] !== winner.parentMarket.participantCount || $[25] !== winner.parentMarket.title) {
    t6 = <WinnerAboutCard name={winner.label} marketTitle={winner.parentMarket.title} marketPubkey={marketPubkey} participantCount={winner.parentMarket.participantCount} className="order-6 lg:order-none" />;
    $[22] = marketPubkey;
    $[23] = winner.label;
    $[24] = winner.parentMarket.participantCount;
    $[25] = winner.parentMarket.title;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = <div data-testid="winner-detail-sidebar" className={t1}>{t2}{t3}{t4}{t5}{t6}</div>;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
}
export interface WinnerDetailLayoutProps {
  marketPubkey: string;
  mint: string;
  canonicalPath: string;
}

/** Winner detail owns one indexed outcome and its sole current pool snapshot. */
export function WinnerDetailLayout(t0) {
  const $ = _c(58);
  const {
    marketPubkey,
    mint,
    canonicalPath
  } = t0;
  const {
    isDemoMode
  } = useDemoMode();
  const hydrated = useHydrated();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  let t1;
  if ($[0] !== mint) {
    t1 = {
      outcomeMint: mint
    };
    $[0] = mint;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const winnerQuery = useGetWinner(t1);
  const winner = winnerQuery.data;
  const t2 = winner !== undefined;
  let t3;
  if ($[2] !== mint || $[3] !== t2) {
    t3 = {
      name: "winner_detail",
      isReady: t2,
      instanceKey: mint
    };
    $[2] = mint;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useRoutePerf(t3);
  if (isDemoMode && !winnerQuery.isPending && winner === undefined) {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DemoNotFoundCard kind="market" />;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (winner === undefined || winner.parentMarket === undefined || winner.parentMarket.pubkey !== marketPubkey) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex flex-col gap-4"><Skeleton className="h-28 w-full rounded-card" /><Skeleton className="h-96 w-full rounded-card" /></div>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t4;
  if ($[7] !== winner) {
    t4 = winnerTradeDataFromRow(winner);
    $[7] = winner;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const tradeData = t4;
  let t5;
  if ($[9] !== winner.imageUrl) {
    t5 = winner.imageUrl !== "" ? {
      imageUrl: winner.imageUrl
    } : {};
    $[9] = winner.imageUrl;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5 || $[12] !== winner.label || $[13] !== winner.mint || $[14] !== winner.outcomeIndex) {
    t6 = [{
      index: winner.outcomeIndex,
      label: winner.label,
      mint: winner.mint,
      ...t5
    }];
    $[11] = t5;
    $[12] = winner.label;
    $[13] = winner.mint;
    $[14] = winner.outcomeIndex;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const tradeFormOutcomes = t6;
  let t7;
  if ($[16] !== winnerQuery) {
    t7 = () => {
      winnerQuery.refetch();
    };
    $[16] = winnerQuery;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7 || $[19] !== tradeData || $[20] !== winnerQuery.isError || $[21] !== winnerQuery.isFetching || $[22] !== winnerQuery.isPending) {
    t8 = {
      tradeData,
      marketDataStale: winnerQuery.isError,
      marketDataPending: winnerQuery.isPending,
      marketDataRefreshing: winnerQuery.isFetching,
      onRetryMarketData: t7
    };
    $[18] = t7;
    $[19] = tradeData;
    $[20] = winnerQuery.isError;
    $[21] = winnerQuery.isFetching;
    $[22] = winnerQuery.isPending;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const tradeReserveProps = t8;
  let t9;
  if ($[24] !== winner.change1hBps || $[25] !== winner.change24hBps || $[26] !== winner.cpQuoteReserveD6 || $[27] !== winner.effectiveMarketCapD6 || $[28] !== winner.holderCount || $[29] !== winner.price || $[30] !== winner.volumeTotal) {
    t9 = {
      marketCap: winner.effectiveMarketCapD6,
      price: winner.price,
      volumeTotal: winner.volumeTotal,
      liquidity: winner.cpQuoteReserveD6,
      holderCount: winner.holderCount,
      change1hBps: winner.change1hBps,
      change24hBps: winner.change24hBps
    };
    $[24] = winner.change1hBps;
    $[25] = winner.change24hBps;
    $[26] = winner.cpQuoteReserveD6;
    $[27] = winner.effectiveMarketCapD6;
    $[28] = winner.holderCount;
    $[29] = winner.price;
    $[30] = winner.volumeTotal;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const statsSnapshot = t9;
  const mountDesktopTrade = !hydrated || isDesktop;
  const mountMobileTrade = !hydrated || !isDesktop;
  let t10;
  if ($[32] !== canonicalPath || $[33] !== tradeData || $[34] !== winner) {
    t10 = <WinnerLeftColumn winner={winner} canonicalPath={canonicalPath} poolSnapshot={tradeData} />;
    $[32] = canonicalPath;
    $[33] = tradeData;
    $[34] = winner;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== canonicalPath || $[37] !== mountDesktopTrade || $[38] !== statsSnapshot || $[39] !== tradeData || $[40] !== tradeFormOutcomes || $[41] !== tradeReserveProps || $[42] !== winner) {
    t11 = <WinnerRightSidebar winner={winner} canonicalPath={canonicalPath} statsSnapshot={statsSnapshot} tradeFormOutcomes={tradeFormOutcomes} tradeReserveProps={tradeReserveProps} tradeData={tradeData} mountTradeForm={mountDesktopTrade} />;
    $[36] = canonicalPath;
    $[37] = mountDesktopTrade;
    $[38] = statsSnapshot;
    $[39] = tradeData;
    $[40] = tradeFormOutcomes;
    $[41] = tradeReserveProps;
    $[42] = winner;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t10 || $[45] !== t11) {
    t12 = <div data-testid="winner-detail-grid" className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-6">{t10}{t11}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== marketPubkey || $[48] !== mountMobileTrade || $[49] !== tradeFormOutcomes || $[50] !== tradeReserveProps || $[51] !== winner.outcomeIndex || $[52] !== winner.parentMarket) {
    t13 = mountMobileTrade ? <WinnerMobileTrade marketAddress={marketPubkey} outcomes={tradeFormOutcomes} selectedOutcomeIndex={winner.outcomeIndex} reserveProps={tradeReserveProps} marketTitle={winner.parentMarket.title} /> : null;
    $[47] = marketPubkey;
    $[48] = mountMobileTrade;
    $[49] = tradeFormOutcomes;
    $[50] = tradeReserveProps;
    $[51] = winner.outcomeIndex;
    $[52] = winner.parentMarket;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <WinnerInfoModal />;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== t12 || $[56] !== t13) {
    t15 = <TradeExecutorProvider><div>{t12}{t13}{t14}</div></TradeExecutorProvider>;
    $[55] = t12;
    $[56] = t13;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  return t15;
}